import { type ComponentProps, createContext, type ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Plus as PlusIcon, Trash2 as TrashIcon } from "@tamagui/lucide-icons";
import { Body1, Body2, Button, Card, Combobox, CurrencyInput, DateField, DatePicker, dateTextToIso, FieldError, formatDate, formatIsoDate, formatSsn, Input, Select, SelectionGroup, SignatureInput, Subtitle2, Subtitle3, UploadSlot, type UploadSlotFile, useFilePicker, XStack, YStack } from "@medbillai/ui";
import { MERGEABLE_SUPPORTING_MEDIA_TYPES } from "@medbillai/schemas";
import { useUploadDocumentPicker } from "../../components/upload-document-sheet/UploadDocumentPickerProvider";
import type { UploadFile } from "../../provider/documents";
import { type BoundField, visibleFieldError, visibleRequiredError } from "./types";

// Whether a submit has been attempted in the current form scope, so the empty
// required cue reveals on unvisited fields the way blur does. Provided per
// section by the FA wizard (a submit-attempt is section-scoped there — the form
// spans all sections, so the raw global counter would light the next section on
// arrival). Defaults false: consumers that don't provide it (onboarding, HIPAA,
// chat) drive their required fields with blocking schemas, whose submit errors
// surface through `visibleFieldError`, never this cue.
// What an upload slot offers is the FA packet merger's capability, not the file
// picker's own default: the two agreeing by coincidence is what let a phone
// photo through to a submit that then froze an unfileable ref. Module-level so
// the identity is stable across renders.
const ACCEPTED_UPLOAD_MEDIA_TYPES = [...MERGEABLE_SUPPORTING_MEDIA_TYPES];
const SubmitAttemptedContext = createContext(false);
export const SubmitAttemptedProvider = SubmitAttemptedContext.Provider;
const useSubmitAttempted = () => useContext(SubmitAttemptedContext);

// Adapters, not new visuals: each binds a TanStack field to an existing v2
// controlled primitive so screens never hand-wire value/onChange/error.

// Distributes the Omit across a prop union so a component's "label XOR
// aria-label" discriminant survives (a plain Omit collapses it).
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

// Binding owns the controlled value + validation channel; trailing-icon
// affordances don't apply to a plain form text field.
type TextFieldProps = {
  field: BoundField<string>;
  help?: string;
} & Omit<ComponentProps<typeof Input>, "value" | "onChangeText" | "onBlur" | "validation" | "helperText" | "clearable" | "onClear" | "rightIcon" | "rightIconRef" | "onRightIconPress" | "rightIconLabel">;
export function FormTextField({
  field,
  help,
  required,
  ...rest
}: TextFieldProps) {
  const submitAttempted = useSubmitAttempted();
  const errorMessage = visibleFieldError(field.state.meta) ?? visibleRequiredError(field.state.meta, field.state.value === "", required, submitAttempted);
  return <Input value={field.state.value} onChangeText={field.handleChange} onBlur={field.handleBlur} required={required} validation={errorMessage ? "error" : "none"} helperText={errorMessage ?? help} {...rest} data-sentry-element="Input" data-sentry-component="FormTextField" data-sentry-source-file="fields.tsx" />;
}

// Single- or multi-select dropdown; the DS Select owns the trigger, menu, and
// keyboard. `multiple` flips it to a count-in-trigger control with the picks as
// removable chips below. One adapter for both so the two never drift.
type SelectFieldProps = {
  options: {
    value: string;
    label: string;
  }[];
  label: string;
  help?: string;
  required?: boolean;
  testID?: string;
} & ({
  multiple?: false;
  field: BoundField<string>;
} | {
  multiple: true;
  field: BoundField<string[]>;
});
export function FormSelectField(props: SelectFieldProps) {
  const {
    options,
    label,
    help,
    required,
    testID
  } = props;
  const submitAttempted = useSubmitAttempted();
  const isEmpty = props.multiple ? props.field.state.value.length === 0 : props.field.state.value === "";
  const errorMessage = visibleFieldError(props.field.state.meta) ?? visibleRequiredError(props.field.state.meta, isEmpty, required, submitAttempted);
  const validation = errorMessage ? "error" : "none";
  return <YStack testID={testID} data-sentry-element="YStack" data-sentry-component="FormSelectField" data-sentry-source-file="fields.tsx">
      {props.multiple ? <Select multiple options={options} label={label} required={required} values={props.field.state.value}
    // A pick is a complete interaction (unlike a half-typed value), so blur
    // alongside the change: constraint errors (maxItems) reveal on the
    // offending pick instead of waiting for a submit attempt.
    onValuesChange={value => {
      props.field.handleChange(value);
      props.field.handleBlur();
    }} onBlur={props.field.handleBlur} validation={validation} helperText={errorMessage ?? help} /> : <Select options={options} label={label} required={required} value={props.field.state.value} onValueChange={value => {
      props.field.handleChange(value);
      props.field.handleBlur();
    }}
    // Dismissing without a pick marks the field touched so an empty
    // required select reveals its message on blur, not only at submit.
    onBlur={props.field.handleBlur} validation={validation} helperText={errorMessage ?? help} />}
    </YStack>;
}

// Date input: MM/DD/YYYY mask + numeric keyboard, matching the v2 date field.
type DateFieldProps = {
  field: BoundField<string>;
  label?: string;
  testID?: string;
};
export function FormDateField({
  field,
  label,
  testID
}: DateFieldProps) {
  const errorMessage = visibleFieldError(field.state.meta);
  return <Input label={label} testID={testID} value={field.state.value} onChangeText={text => field.handleChange(formatDate(text))} onBlur={field.handleBlur} validation={errorMessage ? "error" : "none"} helperText={errorMessage} placeholder="MM/DD/YYYY" keyboardType="number-pad" maxLength={10} data-sentry-element="Input" data-sentry-component="FormDateField" data-sentry-source-file="fields.tsx" />;
}

// Text-entry calendar date that stores canonical ISO (YYYY-MM-DD) in form state;
// DS DateField owns the mask and the parse. Text that parses to no calendar date
// is committed raw — committing "" would silently drop the input and read as an
// unanswered field, whereas the raw text is what a date validator rejects with
// an inline message. Contrast FormDateField, which stores the raw MM/DD/YYYY
// string for the HIPAA release, whose form-state is the document content.
// Display seeds from the stored value at mount (ISO re-masked, raw text as-is)
// and needs no external-value sync: hydration fills the form before mount and
// section nav re-mounts the field.
type IsoDateFieldProps = {
  field: BoundField<string>;
  label?: string;
  help?: string;
  required?: boolean;
  testID?: string;
};
export function FormIsoDateField({
  field,
  label,
  help,
  required,
  testID
}: IsoDateFieldProps) {
  const submitAttempted = useSubmitAttempted();
  const errorMessage = visibleFieldError(field.state.meta) ?? visibleRequiredError(field.state.meta, field.state.value === "", required, submitAttempted);
  const [display, setDisplay] = useState(() => formatIsoDate(field.state.value));
  return <DateField label={label} required={required} testID={testID} value={display} onValueChange={text => {
    setDisplay(text);
    field.handleChange(dateTextToIso(text) ?? text);
  }} onBlur={field.handleBlur} validation={errorMessage ? "error" : "none"} helperText={errorMessage ?? help} data-sentry-element="DateField" data-sentry-component="FormIsoDateField" data-sentry-source-file="fields.tsx" />;
}

// Boolean as a Yes/No pill pair — the single boolean control (a Switch reads as an
// immediate action, wrong for an on-submit form). `undefined` renders neither tile
// selected, so an unanswered question is distinct from an answered "No". Labels
// default to Yes/No; a consumer whose storage isn't a native boolean (e.g. the
// HIPAA release's string form-state) bridges to a boolean binding at its call site.
type BooleanFieldProps = {
  field: BoundField<boolean | undefined>;
  label?: string;
  help?: string;
  required?: boolean;
  trueLabel?: string;
  falseLabel?: string;
  testID?: string;
};
export function FormBooleanField({
  field,
  label,
  help,
  required,
  trueLabel = "Yes",
  falseLabel = "No",
  testID
}: BooleanFieldProps) {
  const value = field.state.value;
  const submitAttempted = useSubmitAttempted();
  const errorMessage = visibleFieldError(field.state.meta) ?? visibleRequiredError(field.state.meta, value === undefined, required, submitAttempted);
  return <YStack gap="$sp2" testID={testID} data-sentry-element="YStack" data-sentry-component="FormBooleanField" data-sentry-source-file="fields.tsx">
      {label ? <Body1>{label}</Body1> : null}
      <SelectionGroup mode="radio" value={value === undefined ? "" : value ? "true" : "false"} onChange={selected => field.handleChange(selected === "true")} flexDirection="row" gap="$sp2" data-sentry-element="SelectionGroup" data-sentry-source-file="fields.tsx">
        <SelectionGroup.Tile value="true" label={trueLabel} flex={1} testID={testID ? `${testID}.yes` : undefined} data-sentry-element="SelectionGroup.Tile" data-sentry-source-file="fields.tsx" />
        <SelectionGroup.Tile value="false" label={falseLabel} flex={1} testID={testID ? `${testID}.no` : undefined} data-sentry-element="SelectionGroup.Tile" data-sentry-source-file="fields.tsx" />
      </SelectionGroup>
      {!errorMessage && help ? <Body2 color="$textSubtle">{help}</Body2> : null}
      <FieldError message={errorMessage} data-sentry-element="FieldError" data-sentry-source-file="fields.tsx" />
    </YStack>;
}
type SignatureFieldProps = {
  field: BoundField<string | null>;
  help?: string;
  required?: boolean;
} & Omit<ComponentProps<typeof SignatureInput>, "saveSignature" | "error" | "helperText">;
export function FormSignatureField({
  field,
  help,
  required,
  ...rest
}: SignatureFieldProps) {
  const submitAttempted = useSubmitAttempted();
  const errorMessage = visibleFieldError(field.state.meta) ?? visibleRequiredError(field.state.meta, field.state.value === null, required, submitAttempted);
  return <SignatureInput saveSignature={field.handleChange} error={errorMessage} helperText={help} {...rest} data-sentry-element="SignatureInput" data-sentry-component="FormSignatureField" data-sentry-source-file="fields.tsx" />;
}

// Money input: integer cents. The empty value is `null` (the binding bridges to
// CurrencyInput's `undefined`) so the form keeps `value` a required key.
type MoneyFieldProps = {
  field: BoundField<number | null>;
  help?: string;
  // Presence-cue only: CurrencyInput has no asterisk concept (the money label
  // is rendered by SpecFields' `Labeled` wrapper), so this never reaches it.
  required?: boolean;
} & Omit<ComponentProps<typeof CurrencyInput>, "value" | "onValueChange" | "validation" | "helperText">;
export function FormMoneyField({
  field,
  help,
  required,
  ...rest
}: MoneyFieldProps) {
  const submitAttempted = useSubmitAttempted();
  const errorMessage = visibleFieldError(field.state.meta) ?? visibleRequiredError(field.state.meta, field.state.value === null, required, submitAttempted);
  return <CurrencyInput value={field.state.value ?? undefined} onValueChange={cents => field.handleChange(cents ?? null)} onBlur={field.handleBlur} validation={errorMessage ? "error" : "none"} helperText={errorMessage ?? help} {...rest} data-sentry-element="CurrencyInput" data-sentry-component="FormMoneyField" data-sentry-source-file="fields.tsx" />;
}

// Single-select combobox (searchable): for long option lists where tappable
// cards would be unwieldy. `options` + an accessible name supplied by the caller.
type ComboboxFieldProps = {
  field: BoundField<string>;
} & DistributiveOmit<ComponentProps<typeof Combobox>,
// Single-select only (BoundField<string>): the multi props are stripped so the
// hardcoded value/onValueChange below always lands on the single union arm.
"value" | "onValueChange" | "onBlur" | "validation" | "helperText" | "multiple" | "values" | "onValuesChange">;
export function FormComboboxField({
  field,
  ...rest
}: ComboboxFieldProps) {
  const errorMessage = visibleFieldError(field.state.meta);
  return <Combobox value={field.state.value} onValueChange={field.handleChange} onBlur={field.handleBlur} validation={errorMessage ? "error" : "none"} helperText={errorMessage} {...rest} data-sentry-element="Combobox" data-sentry-component="FormComboboxField" data-sentry-source-file="fields.tsx" />;
}

// ISO YYYY-MM-DD <-> Date, parsed/formatted in local time so the calendar day
// the user taps is the day we store (no UTC off-by-one).
const isoToDate = (iso: string): Date => new Date(`${iso}T00:00:00`);
const dateToIso = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

// Calendar date picker storing an ISO string (the empty value is "").
type DatePickerFieldProps = {
  field: BoundField<string>;
} & DistributiveOmit<ComponentProps<typeof DatePicker>, "value" | "onValueChange" | "validation" | "helperText">;
export function FormDatePickerField({
  field,
  ...rest
}: DatePickerFieldProps) {
  const errorMessage = visibleFieldError(field.state.meta);
  return <DatePicker value={field.state.value ? isoToDate(field.state.value) : null} onValueChange={date => field.handleChange(date ? dateToIso(date) : "")} validation={errorMessage ? "error" : "none"} helperText={errorMessage} {...rest} data-sentry-element="DatePicker" data-sentry-component="FormDatePickerField" data-sentry-source-file="fields.tsx" />;
}
type SsnFieldProps = {
  field: BoundField<string>;
  label?: string;
  help?: string;
  required?: boolean;
  testID?: string;
};
export function FormSsnField({
  field,
  label,
  help,
  required,
  testID
}: SsnFieldProps) {
  const submitAttempted = useSubmitAttempted();
  const errorMessage = visibleFieldError(field.state.meta) ?? visibleRequiredError(field.state.meta, field.state.value === "", required, submitAttempted);
  return <Input label={label} required={required} testID={testID} value={field.state.value} onChangeText={text => field.handleChange(formatSsn(text))} onBlur={field.handleBlur} validation={errorMessage ? "error" : "none"} helperText={errorMessage ?? help} placeholder="XXX-XX-XXXX" keyboardType="number-pad" maxLength={11} data-sentry-element="Input" data-sentry-component="FormSsnField" data-sentry-source-file="fields.tsx" />;
}

// An uploaded document is opaque to the adapter: a reference to store plus a
// name to show and an optional byte size for the file row. The persisted FA
// doc-reference shape tolerates extra keys (the server's document schema is
// passthrough), so the size rides on the reference and survives draft restore.
export type UploadedDocument = {
  id: string;
  name: string;
  sizeBytes?: number;
};
type UploadAsset = {
  uri: string;
  name: string;
  mimeType?: string | null;
  size?: number;
  file?: File;
  blob?: Blob;
};

// Uploads the picked files and resolves to the references to store. Injected so
// the adapter is decoupled from the case-scoped upload provider and the network.
// A rejection surfaces its `message` to the user on the failed row; reporting the
// failure (Sentry etc.) is the handler's job, not this presentational adapter's.
export type DocumentUploadHandler = (files: UploadAsset[]) => Promise<UploadedDocument[]>;
type DocumentFieldProps = {
  field: BoundField<UploadedDocument[]>;
  onUpload: DocumentUploadHandler;
  // Fires after the committed document list changes (a finished upload or a
  // removal), with the full new list. Form state only lives in this session;
  // a host that must not lose uploads on close persists them here.
  onCommitted?: (documents: UploadedDocument[]) => void;
  // The document being requested (e.g. "Photo ID"); becomes the UploadSlot title.
  label: string;
  // What to send, in the applicant's terms (e.g. "Driver's license or
  // passport"); becomes the UploadSlot subtitle.
  help?: string;
  required?: boolean;
  maxItems?: number;
  testID?: string;
};

// Tracks a single file that is in-flight or has errored; `errorText` carries the
// rejection's message so the row shows why it failed.
type PendingFile = {
  asset: UploadAsset;
  status: "uploading" | "error";
  errorText?: string;
};

// Web drag-drop hands over raw `File`s; the upload seam speaks picker assets.
const fileToAsset = (file: File): UploadAsset => ({
  uri: URL.createObjectURL(file),
  name: file.name,
  mimeType: file.type,
  size: file.size,
  file
});
const uploadFileToAsset = (file: UploadFile): UploadAsset => ({
  uri: "",
  name: file.name,
  mimeType: file.contentType,
  size: file.sizeBytes,
  blob: file.blob
});

// Web assets carry `blob:` preview URLs that pin the file in memory until
// released; native URIs pass through untouched.
const revokeAssetUri = (asset: UploadAsset) => {
  if (asset.uri.startsWith("blob:")) {
    URL.revokeObjectURL(asset.uri);
  }
};
const normalizeMaxItems = (maxItems: number | undefined) => {
  if (maxItems === undefined) {
    return undefined;
  }
  return Number.isFinite(maxItems) ? Math.max(0, Math.floor(maxItems)) : 0;
};
export function FormDocumentField({
  field,
  onUpload,
  onCommitted,
  label,
  help,
  required,
  maxItems,
  testID
}: DocumentFieldProps) {
  const documents = field.state.value;
  const normalizedMaxItems = normalizeMaxItems(maxItems);
  const submitAttempted = useSubmitAttempted();
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [pickerError, setPickerError] = useState<string>();
  const openDocumentPicker = useUploadDocumentPicker();

  // Updated immediately on each per-file commit so sequential uploads see the
  // previous file's result before React re-renders. Also syncs on render to pick
  // up user-initiated removals.
  const committedRef = useRef(documents);
  committedRef.current = documents;

  // Blob: previews outstanding when unmounted would otherwise leak memory.
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  // Tracks real mount/unmount so an in-flight onUpload never resolves into
  // setState / handleChange after the field is gone (e.g. wizard closed mid-upload).
  // Re-set on each setup so StrictMode's simulated remount restores it.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pendingRef.current.forEach(p => {
        revokeAssetUri(p.asset);
      });
    };
  }, []);
  const uploadSingleFile = async (asset: UploadAsset) => {
    try {
      const results = await onUpload([asset]);
      if (!mountedRef.current) {
        return;
      }
      setPending(prev => prev.filter(p => p.asset !== asset));
      const next = [...committedRef.current, ...results];
      committedRef.current = next;
      field.handleChange(next);
      onCommitted?.(next);
      revokeAssetUri(asset);
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      setPending(prev => prev.map(p => p.asset === asset ? {
        asset,
        status: "error" as const,
        errorText: error instanceof Error ? error.message : undefined
      } : p));
    }
  };
  const uploadAssets = async (assets: UploadAsset[]) => {
    setPending(prev => [...prev, ...assets.map(asset => ({
      asset,
      status: "uploading" as const
    }))]);
    for (const asset of assets) {
      await uploadSingleFile(asset);
    }
  };

  // Assets past the remaining capacity never reach `onUpload` — uploading and
  // then dropping them would orphan stored files the form never references.
  const handlePicked = (assets: UploadAsset[]) => {
    setPickerError(undefined);
    // Block new picks while any file is uploading; a file dialog opened
    // beforehand can still deliver late.
    if (pending.some(p => p.status === "uploading")) {
      assets.forEach(revokeAssetUri);
      return;
    }
    const capacity = normalizedMaxItems === undefined ? assets.length : Math.max(0, normalizedMaxItems - committedRef.current.length);
    const accepted = assets.slice(0, capacity);
    assets.slice(capacity).forEach(revokeAssetUri);
    if (accepted.length > 0) {
      // A new pick replaces any error rows from the previous pick.
      pending.forEach(p => {
        revokeAssetUri(p.asset);
      });
      setPending([]);
      void uploadAssets(accepted);
    }
  };
  const {
    pickFile
  } = useFilePicker({
    accept: ACCEPTED_UPLOAD_MEDIA_TYPES,
    multiple: normalizedMaxItems !== 1,
    onFilesSelected: handlePicked
  });
  const chooseDocument = openDocumentPicker ? () => {
    setPickerError(undefined);
    openDocumentPicker({
      title: `Upload ${label}`,
      // Mirror the field's own picker: the mergeable set, field-driven
      // multiplicity — not the sheet's chat default (single PDF on web).
      filePickerOptions: {
        accept: ACCEPTED_UPLOAD_MEDIA_TYPES,
        multiple: normalizedMaxItems !== 1
      },
      onFilesLoaded: files => {
        handlePicked(files.map(uploadFileToAsset));
        return Promise.resolve();
      },
      onUploadError: error => {
        // An unsupported type reports twice — the friendly copy, then the
        // raw throw. First write wins so the user keeps the readable one.
        setPickerError(current => current ?? error.message ?? error.error?.message ?? "Unable to load this document. Please try again.");
      }
    });
  } : pickFile;
  const files: UploadSlotFile[] = [...documents.map(document => ({
    id: document.id,
    name: document.name,
    status: "done" as const,
    // A restored ref may carry a foreign sizeBytes shape (the persisted
    // answer schema is passthrough); only a real byte count reaches the row.
    sizeBytes: typeof document.sizeBytes === "number" && Number.isSafeInteger(document.sizeBytes) && document.sizeBytes >= 0 ? document.sizeBytes : undefined
  })), ...pending.map((pf, index) => ({
    id: `pending-${index}`,
    name: pf.asset.name,
    status: pf.status,
    errorText: pf.status === "error" ? pf.errorText ?? "Upload failed" : undefined
  }))];
  const removeAt = (index: number) => {
    if (index < documents.length) {
      const next = documents.filter((_, i) => i !== index);
      committedRef.current = next;
      field.handleChange(next);
      onCommitted?.(next);
      return;
    }
    const pendingIndex = index - documents.length;
    const removed = pending[pendingIndex];
    if (removed) {
      revokeAssetUri(removed.asset);
    }
    setPending(prev => prev.filter((_, i) => i !== pendingIndex));
  };
  const multiplicity = normalizedMaxItems === 1 ? {
    multiple: false as const
  } : {
    multiple: true as const,
    maxFiles: normalizedMaxItems
  };
  const anyUploading = pending.some(p => p.status === "uploading");
  const atCapacity = normalizedMaxItems !== undefined && documents.length >= normalizedMaxItems;
  const disableAdd = anyUploading || atCapacity;

  // Empty for required-validation only when nothing is committed and nothing is
  // mid-flight — a pending/error row already speaks for itself.
  const fieldError = visibleFieldError(field.state.meta) ?? visibleRequiredError(field.state.meta, documents.length === 0 && pending.length === 0, required, submitAttempted);
  const shownError = pickerError || fieldError;
  return <YStack gap="$sp2" testID={testID} data-sentry-element="YStack" data-sentry-component="FormDocumentField" data-sentry-source-file="fields.tsx">
      <UploadSlot title={label} subtitle={help} required={required} validation={shownError ? "error" : "none"} helperText={shownError} files={files} onAddPress={disableAdd ? undefined : chooseDocument} onFilesDropped={disableAdd ? undefined : dropped => handlePicked(dropped.map(fileToAsset))} onRemove={(_file, index) => removeAt(index)} onRetry={(_file, index) => {
      const pf = pending[index - documents.length];
      if (!pf || pf.status !== "error") {
        return;
      }
      setPending(prev => prev.map(p => p.asset === pf.asset ? {
        ...p,
        status: "uploading" as const
      } : p));
      void uploadSingleFile(pf.asset);
    }} {...multiplicity} data-sentry-element="UploadSlot" data-sentry-source-file="fields.tsx" />
    </YStack>;
}

// A repeating group: the add / remove / maxItems chrome around an array field. Row
// contents stay the consumer's: `renderRow(index)` mounts the nested `form.Field`s
// (one per sub-field) so each sub-field keeps its own validation routing, which a
// single whole-array binding could not. Route `onRemove` through the array field's
// `removeValue` — TanStack re-keys per-row meta (touched / errors) only via its own
// array mutators, not on a whole-array `handleChange`. Appends have no meta to
// shift, so `onAdd` may simply `handleChange` the extended array.
type GroupFieldProps = {
  field: BoundField<unknown[]>;
  onAdd: () => void;
  onRemove: (index: number) => void;
  renderRow: (index: number) => ReactNode;
  label?: string;
  itemLabel?: string;
  addLabel?: string;
  maxItems?: number;
  testID?: string;
};
export function FormGroupField({
  field,
  onAdd,
  onRemove,
  renderRow,
  label,
  itemLabel,
  addLabel = "Add",
  maxItems,
  testID
}: GroupFieldProps) {
  const count = field.state.value.length;
  const errorMessage = visibleFieldError(field.state.meta);
  const atMax = maxItems !== undefined && count >= maxItems;
  return <YStack gap="$sp3" testID={testID} data-sentry-element="YStack" data-sentry-component="FormGroupField" data-sentry-source-file="fields.tsx">
      <XStack ai="center" jc="space-between" gap="$sp2" data-sentry-element="XStack" data-sentry-source-file="fields.tsx">
        {label ? <Subtitle3>{label}</Subtitle3> : null}
        {count > 0 ? <Body2 color="$textSubtle">{count} added</Body2> : null}
      </XStack>
      {Array.from({
      length: count
    }, (_, index) =>
    // Rows are positional with no stable id; index is the key.
    <Card key={index} size="large" backgroundColor="$surface0" testID={testID ? `${testID}.row.${index}` : undefined}>
          <YStack gap="$sp2">
            <XStack ai="center" jc="space-between" gap="$sp2">
              <Subtitle2 color="$textDefault">
                {itemLabel ? `${itemLabel} ${index + 1}` : `Item ${index + 1}`}
              </Subtitle2>
              <Button variant="tertiary" iconLeft={TrashIcon} fillWidth={false} onPress={() => onRemove(index)} testID={testID ? `${testID}.row.${index}.remove` : undefined}>
                Remove
              </Button>
            </XStack>
            {renderRow(index)}
          </YStack>
        </Card>)}
      <Button variant="secondary" iconLeft={PlusIcon} disabled={atMax} onPress={onAdd} testID={testID ? `${testID}.add` : undefined} data-sentry-element="Button" data-sentry-source-file="fields.tsx">
        {addLabel}
      </Button>
      {errorMessage ? <FieldError message={errorMessage} /> : null}
    </YStack>;
}